import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError, uploadFileToSignedUrl } from '@l2u/ui/lib/api.ts';
import { downloadSignedUrl } from '@l2u/ui/lib/download.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { EmptyState } from '@l2u/ui/components/EmptyState.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { Field, TextInput } from '@l2u/ui/components/Field.tsx';
import { FileDrop } from '@l2u/ui/components/FileDrop.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import type { CompanyDocument } from '@l2u/ui/lib/types.ts';

// Shared so the header row and body rows can never drift out of alignment.
const GRID_COLS = 'minmax(230px,2.4fr) minmax(160px,1.6fr) 90px 120px 130px 210px';

type SortKey = 'name-asc' | 'name-desc' | 'category' | 'version-desc';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'name-asc', label: 'Name (A–Z)' },
  { value: 'name-desc', label: 'Name (Z–A)' },
  { value: 'category', label: 'Category' },
  { value: 'version-desc', label: 'Version (newest)' },
];

export function AdminDocuments() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sort, setSort] = useState<SortKey>('name-asc');

  const documents = useMemo(() => data?.documents ?? [], [data]);

  const categories = useMemo(
    () => [...new Set(documents.map((d) => d.category).filter(Boolean))].sort(),
    [documents],
  );

  const visibleDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = documents.filter(
      (d) =>
        (q === '' || d.name.toLowerCase().includes(q)) &&
        (categoryFilter === 'all' || d.category === categoryFilter),
    );
    const cmp: Record<SortKey, (a: CompanyDocument, b: CompanyDocument) => number> = {
      'name-asc': (a, b) => a.name.localeCompare(b.name),
      'name-desc': (a, b) => b.name.localeCompare(a.name),
      category: (a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name),
      'version-desc': (a, b) =>
        b.version.localeCompare(a.version, undefined, { numeric: true }) ||
        a.name.localeCompare(b.name),
    };
    return [...filtered].sort(cmp[sort]);
  }, [documents, search, categoryFilter, sort]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: Partial<CompanyDocument> }) =>
      adminApi.patch(`/api/documents/${input.id}`, input.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });

  // Preview reuses the download signed URL; browsers render PDFs inline in a new tab.
  async function previewDocument(d: CompanyDocument) {
    const res = await adminApi
      .get<{ url: string }>(`/api/documents/${d.id}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function downloadDocument(d: CompanyDocument) {
    const res = await adminApi
      .get<{ url: string }>(`/api/documents/${d.id}/download-url`)
      .catch(() => null);
    if (res?.url) downloadSignedUrl(res.url, documentFileName(d));
  }

  function deleteDocument(d: CompanyDocument) {
    void (async () => {
      if (
        await confirm({
          title: 'Delete document',
          body: (
            <>
              Are you sure you want to delete <span className="font-semibold">{d.name}</span>? This
              can&apos;t be undone.
            </>
          ),
          confirmLabel: 'Delete',
          danger: true,
        })
      )
        deleteMutation.mutate(d.id);
    })();
  }

  if (isLoading) return <CenteredSpinner />;

  const adoptableCount = documents.filter((d) => d.is_adoptable).length;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            Compliance Library
          </div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">Documents</h1>
          <p className="mt-1 text-sm text-slate-500">
            Adoptable policies and safety documents available across all stations.
          </p>
        </div>
        <Btn className="rounded-[11px] shadow-md" onClick={() => setUploadOpen(true)}>
          + Upload document
        </Btn>
      </div>

      {documents.length === 0 ? (
        <EmptyState
          title="No documents yet"
          description="Upload safety policies that vendors can adopt during the questionnaire."
          action={<Btn onClick={() => setUploadOpen(true)}>+ Upload document</Btn>}
        />
      ) : (
        <>
          {/* Stat strip */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label="Total documents" value={documents.length} />
            <StatCard label="Adoptable" value={adoptableCount} accent />
            <StatCard label="Categories" value={categories.length} />
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <label className="relative block min-w-[220px] max-w-[400px] flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <SearchIcon />
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search documents…"
                className="h-11 w-full rounded-[11px] border border-[#e4e7eb] bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-[#4D148C] focus:ring-2 focus:ring-[#4D148C]/20"
              />
            </label>
            <SelectWrap>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="h-11 appearance-none rounded-[11px] border border-[#e4e7eb] bg-white pl-4 pr-9 text-sm text-slate-700 outline-none focus:border-[#4D148C]"
              >
                <option value="all">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </SelectWrap>
            <SelectWrap>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="h-11 appearance-none rounded-[11px] border border-[#e4e7eb] bg-white pl-4 pr-9 text-sm text-slate-700 outline-none focus:border-[#4D148C]"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </SelectWrap>
          </div>

          {/* Table card */}
          <div className="overflow-hidden rounded-2xl border border-[#eaecef] bg-white shadow-sm">
            <div className="overflow-x-auto">
              <div className="min-w-[940px]">
                {/* Header row */}
                <div
                  className="grid items-center border-b border-slate-100 bg-[#fafbfc] px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"
                  style={{ gridTemplateColumns: GRID_COLS }}
                >
                  <div>Document</div>
                  <div>Category</div>
                  <div>Version</div>
                  <div>Adoptable</div>
                  <div>Status</div>
                  <div className="text-right">Actions</div>
                </div>

                {/* Body rows */}
                {visibleDocs.map((d) => {
                  const updated = formatDate(d.updated_at);
                  return (
                    <div
                      key={d.id}
                      className="grid items-center border-b border-slate-50 px-6 py-3.5 transition-colors hover:bg-[#faf9fd]"
                      style={{ gridTemplateColumns: GRID_COLS }}
                    >
                      {/* Document */}
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#f3eefe] text-[#4D148C]">
                          <DocIcon />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-slate-900">{d.name}</div>
                          <div className="mt-0.5 text-xs text-slate-400">
                            {fileTypeLabel(d)}
                            {updated ? ` · Updated ${updated}` : ''}
                          </div>
                        </div>
                      </div>
                      {/* Category */}
                      <div>
                        <span
                          className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${categoryPillClass(d.category)}`}
                        >
                          {d.category || 'General'}
                        </span>
                      </div>
                      {/* Version */}
                      <div>
                        <span className="inline-block rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                          v{d.version}
                        </span>
                      </div>
                      {/* Adoptable toggle */}
                      <div>
                        <button
                          role="switch"
                          aria-checked={d.is_adoptable}
                          aria-label={d.is_adoptable ? 'Adoptable' : 'Not adoptable'}
                          disabled={updateMutation.isPending}
                          onClick={() =>
                            updateMutation.mutate({
                              id: d.id,
                              patch: { is_adoptable: !d.is_adoptable },
                            })
                          }
                          className={`relative h-6 w-[42px] rounded-full transition-colors disabled:opacity-60 ${
                            d.is_adoptable ? 'bg-[#4D148C]' : 'bg-slate-300'
                          }`}
                        >
                          <span
                            className="absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow transition-transform"
                            style={{
                              transform: d.is_adoptable ? 'translateX(21px)' : 'translateX(3px)',
                            }}
                          />
                        </button>
                      </div>
                      {/* Status */}
                      <div>
                        {d.current_version ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Current
                          </span>
                        ) : (
                          <button
                            className="text-xs font-medium text-[#4D148C] hover:underline"
                            onClick={() =>
                              updateMutation.mutate({ id: d.id, patch: { current_version: true } })
                            }
                          >
                            Promote
                          </button>
                        )}
                      </div>
                      {/* Actions */}
                      <div className="flex items-center justify-end gap-2">
                        <button
                          title="Preview"
                          aria-label="Preview"
                          onClick={() => void previewDocument(d)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#e4e7eb] text-slate-500 transition-colors hover:border-[#4D148C] hover:text-[#4D148C]"
                        >
                          <EyeIcon />
                        </button>
                        <button
                          onClick={() => void downloadDocument(d)}
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#e4e7eb] px-3 text-xs font-semibold text-slate-600 transition-colors hover:border-[#4D148C] hover:text-[#4D148C]"
                        >
                          <DownloadIcon />
                          Download
                        </button>
                        <button
                          title="Delete"
                          aria-label="Delete"
                          onClick={() => deleteDocument(d)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#f2dede] text-red-600 transition-colors hover:border-red-500 hover:bg-red-50"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Empty-search state (documents exist, but none match the filters) */}
                {visibleDocs.length === 0 && (
                  <div className="px-6 py-14 text-center text-slate-400">
                    <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center text-slate-300">
                      <SearchIcon size={32} />
                    </div>
                    <div className="text-sm font-semibold text-slate-600">
                      No documents match your search
                    </div>
                    <div className="mt-1 text-sm">
                      Try a different keyword or clear the category filter.
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-slate-100 bg-[#fafbfc] px-6 py-3.5 text-xs text-slate-500">
                  <span>
                    Showing {visibleDocs.length} of {documents.length} documents
                  </span>
                  <span className="text-slate-300">Locate2u Compliance</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {(deleteMutation.error || updateMutation.error) && (
        <ErrorMessage error={deleteMutation.error ?? updateMutation.error} />
      )}

      <UploadDocumentModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="rounded-[14px] border border-[#eaecef] bg-white px-5 py-4">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-extrabold tracking-tight ${accent ? 'text-[#4D148C]' : 'text-slate-900'}`}
      >
        {value}
      </div>
    </div>
  );
}

// Native <select> with a custom chevron; appearance-none hides the OS default.
function SelectWrap({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      {children}
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
        ▾
      </span>
    </div>
  );
}

// Instant → local "12 Jun 2026"; mirrors the pattern in DocumentReviewTable. Not
// formatDateOnly (that forces UTC, which is wrong for real timestamps like updated_at).
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fileTypeLabel(d: CompanyDocument): string {
  const ext = /\.([A-Za-z0-9]+)$/.exec(d.file_path)?.[1];
  return ext ? ext.toUpperCase() : 'FILE';
}

function categoryPillClass(category: string): string {
  const c = category.toLowerCase();
  if (c.includes('health') || c.includes('safety')) return 'bg-emerald-50 text-emerald-700';
  if (c.includes('vehicle')) return 'bg-blue-50 text-blue-700';
  return 'bg-slate-100 text-slate-600';
}

function SearchIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

// Saved filename: the display name plus the stored file's extension — the
// storage path ends "<uuid>-<original-name>.<ext>" (buildDocumentPath).
function documentFileName(d: CompanyDocument): string {
  const ext = /\.[A-Za-z0-9]+$/.exec(d.file_path)?.[0] ?? '';
  return `${d.name}${ext}`;
}

function UploadDocumentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState({ name: '', category: '', version: '1.0', is_adoptable: true });
  const [step, setStep] = useState<'idle' | 'uploading' | 'registering'>('idle');
  const [error, setError] = useState<unknown>(null);

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      setStep('uploading');
      const presigned = await adminApi.post<{ path: string; url: string }>(
        '/api/documents/upload-url',
        { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      setStep('registering');
      await adminApi.post('/api/documents', {
        name: meta.name || file.name,
        category: meta.category || 'General',
        version: meta.version || '1.0',
        file_path: presigned.path,
        is_adoptable: meta.is_adoptable,
      });
      await qc.invalidateQueries({ queryKey: ['documents'] });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setStep('idle');
    }
  }

  function reset() {
    setFile(null);
    setMeta({ name: '', category: '', version: '1.0', is_adoptable: true });
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (step === 'idle') {
          reset();
          onClose();
        }
      }}
      title="Upload document"
      footer={
        <>
          <Btn
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={step !== 'idle'}
          >
            Cancel
          </Btn>
          <Btn onClick={submit} loading={step !== 'idle'} disabled={!file || !meta.name}>
            Upload
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <FileDrop
          multiple={false}
          onFiles={(files) => {
            const f = files.item(0);
            if (f) {
              setFile(f);
              setMeta((m) => ({ ...m, name: m.name || f.name.replace(/\.[^.]+$/, '') }));
            }
          }}
          hint="PDF, DOCX, or any policy document"
        />
        {file && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {file.name} ({Math.round(file.size / 1024)} KB)
          </div>
        )}
        <Field label="Name" required>
          <TextInput
            value={meta.name}
            onChange={(e) => setMeta({ ...meta, name: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category" required>
            <TextInput
              value={meta.category}
              onChange={(e) => setMeta({ ...meta, category: e.target.value })}
              placeholder="e.g. Safety"
            />
          </Field>
          <Field label="Version" required>
            <TextInput
              value={meta.version}
              onChange={(e) => setMeta({ ...meta, version: e.target.value })}
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={meta.is_adoptable}
            onChange={(e) => setMeta({ ...meta, is_adoptable: e.target.checked })}
          />
          Allow vendors to adopt this policy
        </label>
        {step === 'uploading' && <div className="text-xs text-slate-500">Uploading file…</div>}
        {step === 'registering' && (
          <div className="text-xs text-slate-500">Recording document…</div>
        )}
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
